import { useState } from "react";
import { NotificationManager } from "react-notifications";
import { DecisionSwal } from "Utils/Utils/DecisionSwal";
import { getErrorFromResponse } from "../../../../../../../../utility/utils";
import { CierresMensualesProduccionApi } from "../../../api/CierresMensualesProduccionApi";
import { ComparativoMensualProduccionApi } from "../api/ComparativoMensualProduccionApi";
import { obtenerMotivoSinCierre } from "../helpers/textosSiguienteCierre";
import { useFiltrosCierreProduccion } from "./useFiltrosCierreProduccion";

export const useComparativoMensualProduccion = ({ regresar }) => {
    const {
        getComparativoMensual,
        descargarComparativoMensualExcel,
        cargando: cargandoComparativo,
    } = ComparativoMensualProduccionApi();

    const {
        getPrevisualizacion,
        registrarCierre,
        cargando: cargandoCierre,
    } = CierresMensualesProduccionApi();

    // lo que abarcaría el cierre y lo que le falta para poder registrarse
    const [previsualizacion, setPrevisualizacion] = useState(null);
    const [comparativo, setComparativo] = useState(null);
    const [filtros, setFiltros] = useState(null);

    // el error se muestra junto a las tablas porque enumera los turnos, bultos
    // o semanas que tienen algo pendiente, y la notificación se va muy rápido
    const [mensajeError, setMensajeError] = useState("");

    // Lo que está en pantalla corresponde a los filtros con los que se consultó:
    // al cambiar uno se quitan el alcance, las tablas y los botones de exportar y
    // cerrar, y la nueva consulta se pide con el botón
    const LimpiarConsulta = () => {
        setMensajeError("");
        setComparativo(null);
        setPrevisualizacion(null);
        setFiltros(null);
    };

    // La previsualización y el comparativo reciben los mismos filtros. La
    // previsualización va primero: si los filtros no arman un cierre válido, su
    // error ya explica por qué y no hace falta pedir el comparativo
    const Consultar = async (params) => {
        LimpiarConsulta();
        setFiltros(params);

        try {
            setPrevisualizacion(await getPrevisualizacion(params));
        } catch (error) {
            const errorMsg = getErrorFromResponse(
                error,
                "Error al obtener el periodo a cerrar",
            );
            NotificationManager.error(errorMsg, "Error", 3000);
            setMensajeError(errorMsg);
            return;
        }

        try {
            setComparativo(await getComparativoMensual(params));
        } catch (error) {
            // el error se deja junto a las tablas y no solo como notificación,
            // porque sin comparativo la pantalla se queda sin qué mostrar
            setMensajeError(
                getErrorFromResponse(
                    error,
                    "Error al obtener el comparativo mensual",
                ),
            );
        }
    };

    // después de guardar algo que cambia el cierre, se consulta de nuevo con los
    // mismos filtros
    const RecargarConsulta = () => {
        if (!filtros) return;

        Consultar(filtros);
    };

    const DescargarExcel = async () => {
        if (!filtros) return;

        try {
            await descargarComparativoMensualExcel(filtros);
        } catch (error) {
            NotificationManager.error(
                getErrorFromResponse(
                    error,
                    "Error al exportar el comparativo mensual",
                ),
                "Error",
                3000,
            );
        }
    };

    const CerrarPeriodo = () => {
        if (!filtros) return;

        DecisionSwal(
            "Al cerrar, las cifras del comparativo quedarán registradas y las " +
                "semanas que abarca no podrán modificarse. ¿Desea cerrar el periodo?",
            "Sí, cerrar",
            "Cancelar",
            _registrar,
        );
    };

    const _registrar = async () => {
        try {
            await registrarCierre(filtros);

            NotificationManager.success(
                "Periodo cerrado correctamente",
                "Éxito",
                3000,
            );

            filtrosCierre.limpiarFiltrosGuardados();
            regresar();
        } catch (error) {
            NotificationManager.error(
                getErrorFromResponse(error, "Error al cerrar el periodo"),
                "Error",
                3000,
            );
        }
    };

    const filtrosCierre = useFiltrosCierreProduccion({
        onConsultar: Consultar,
    });

    // Si el producto padre no puede registrar cierres, la fecha de corte ni
    // siquiera se muestra: en lugar de consultar se deja el motivo como error
    const ConsultarReporte = () => {
        const { siguienteCierre, padreProductoTerminado } = filtrosCierre;

        const motivoSinCierre =
            siguienteCierre &&
            padreProductoTerminado &&
            obtenerMotivoSinCierre(
                siguienteCierre,
                padreProductoTerminado.nombre,
            );

        if (motivoSinCierre) {
            LimpiarConsulta();
            setMensajeError(motivoSinCierre);
            return;
        }

        filtrosCierre.consultarReporte();
    };

    // el comparativo llega sin filas cuando el periodo tiene semanas
    // incompletas o no abarca periodos de inventario de almacén: no hay cifras
    // que congelar ni que exportar
    const hayComparativo =
        Boolean(comparativo) && comparativo.filas.length > 0;

    return {
        ...filtrosCierre,

        previsualizacion,
        comparativo,
        hayComparativo,
        mensajeError,
        filtros,

        consultarReporte: ConsultarReporte,
        limpiarConsulta: LimpiarConsulta,
        recargarConsulta: RecargarConsulta,
        descargarExcel: DescargarExcel,
        cerrarPeriodo: CerrarPeriodo,

        cargando:
            cargandoComparativo ||
            cargandoCierre ||
            filtrosCierre.cargandoSiguienteCierre,
    };
};
